/**
 * Text Fitting Utility
 *
 * Auto-shrinks font size to fit text within a container.
 * Uses DOM measurement for accurate results.
 */

import { TextOverlayData } from '../types';

interface FitTextOptions {
  text: string;
  maxWidth: number;
  maxHeight: number;
  fontFamily: string;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  lineHeight: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  initialFontSize: number;
  minFontSize?: number;
}

interface FitTextResult {
  fontSize: number;
  fits: boolean;
}

/**
 * Measures text and finds the optimal font size to fit within constraints.
 * Uses binary search for efficiency.
 */
export function fitTextToBox(options: FitTextOptions): FitTextResult {
  const {
    text,
    maxWidth,
    maxHeight,
    fontFamily,
    fontWeight,
    fontStyle,
    lineHeight,
    padding,
    initialFontSize,
    minFontSize = 8,
  } = options;

  // Available space after padding
  const availableWidth = maxWidth - padding.left - padding.right;
  const availableHeight = maxHeight - padding.top - padding.bottom;

  if (availableWidth <= 0 || availableHeight <= 0) {
    return { fontSize: minFontSize, fits: false };
  }

  // Create a hidden measurement element
  const measurer = document.createElement('div');
  measurer.style.cssText = `
    position: absolute;
    visibility: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    width: ${availableWidth}px;
    font-family: ${fontFamily};
    font-weight: ${fontWeight};
    font-style: ${fontStyle};
    line-height: ${lineHeight};
  `;
  document.body.appendChild(measurer);

  // Binary search for optimal font size
  let low = minFontSize;
  let high = initialFontSize;
  let bestFit = minFontSize;

  // First check if initial size fits
  measurer.style.fontSize = `${initialFontSize}px`;
  measurer.textContent = text;

  if (measurer.scrollHeight <= availableHeight) {
    // Initial size fits, no need to shrink
    document.body.removeChild(measurer);
    return { fontSize: initialFontSize, fits: true };
  }

  // Binary search for the largest font that fits
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    measurer.style.fontSize = `${mid}px`;
    measurer.textContent = text;

    if (measurer.scrollHeight <= availableHeight) {
      bestFit = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  document.body.removeChild(measurer);

  return {
    fontSize: bestFit,
    fits: bestFit >= minFontSize,
  };
}

/**
 * Fits a text overlay to its container dimensions.
 * Returns the adjusted font size.
 */
export function fitTextOverlay(
  overlay: TextOverlayData,
  containerWidthPx: number,
  containerHeightPx: number
): number {
  const widthPx = (overlay.width / 100) * containerWidthPx;
  const heightPx = overlay.height !== undefined
    ? (overlay.height / 100) * containerHeightPx
    : Infinity; // No height constraint = don't shrink

  if (heightPx === Infinity) {
    return overlay.style.fontSize;
  }

  const padding = {
    top: overlay.style.padding?.top ?? 8,
    right: overlay.style.padding?.right ?? 12,
    bottom: overlay.style.padding?.bottom ?? 8,
    left: overlay.style.padding?.left ?? 12,
  };

  const result = fitTextToBox({
    text: overlay.text,
    maxWidth: widthPx,
    maxHeight: heightPx,
    fontFamily: overlay.style.fontFamily,
    fontWeight: overlay.style.fontWeight,
    fontStyle: overlay.style.fontStyle,
    lineHeight: overlay.style.lineHeight ?? 1.4,
    padding,
    initialFontSize: overlay.style.fontSize,
    minFontSize: 8,
  });

  return result.fontSize;
}
